/**
 * Auth copy — the single place a Cognito failure becomes something a user reads.
 *
 * AL-470 typed the failures so the UI stopped rendering raw AWS prose.
 * AL-472 (finding 3) closes the remaining hole: the typed codes were each given
 * their own wording, so the screen still answered the question "does this
 * account exist?" for anyone who cared to compare responses.
 *
 *   UserNotFoundException      -> "check your username"
 *   UserNotConfirmedException  -> "not yet confirmed, check your email"
 *   NotAuthorizedException     -> a third, longer message
 *
 * Three distinguishable answers is all an enumeration oracle needs. It gains no
 * access; it is the input to credential stuffing and phishing.
 *
 * The pool-level fix (PreventUserExistenceErrors = ENABLED) makes most of these
 * unreachable, but it lives in no IaC — a client created via the API inherits
 * LEGACY — so the UI must not depend on it. UserNotConfirmedException is not
 * among the responses AWS documents as made generic, so it stays reachable
 * either way.
 *
 * Rule for this module: within one context, every failure that could be derived
 * from whether an account exists returns the *same* string. Not a similar one.
 */

import { AuthError, type AuthErrorCode } from '../../services/authService';
import { describePasswordPolicy, type PasswordPolicy } from '../../config/passwordPolicy';

export type AuthMessageContext =
	| 'signIn'
	/** Responding to the NEW_PASSWORD_REQUIRED challenge on first sign-in. */
	| 'newPassword'
	/** Asking for a reset code. */
	| 'resetRequest'
	/** Submitting a code plus a new password. */
	| 'resetConfirm';

/**
 * Codes that are, or can be, a statement about whether an account exists.
 *
 * - `USER_NOT_FOUND` — says so outright.
 * - `USER_NOT_CONFIRMED` — confirms it exists, and its state.
 * - `INVALID_CREDENTIALS` — on a reset request there is no credential to be
 *   wrong, so reaching it at all (a disabled account, for instance) implies the
 *   account is there.
 * - `RESET_NOT_ALLOWED` — "no registered/verified email" is only answerable for
 *   an account that exists.
 */
const EXISTENCE_DERIVED_CODES: readonly AuthErrorCode[] = [
	'INVALID_CREDENTIALS',
	'USER_NOT_FOUND',
	'USER_NOT_CONFIRMED',
	'RESET_NOT_ALLOWED',
];

/**
 * True when surfacing this failure on the reset-request step would answer
 * "does this account exist?".
 *
 * The caller absorbs these and shows the same neutral confirmation it shows for
 * an account that does exist. The accepted cost, called out in AL-472: a
 * mistyped username is told a code was sent and none arrives. Support needs to
 * know before this ships.
 */
export function revealsAccountExistence(err: unknown): boolean {
	return err instanceof AuthError && EXISTENCE_DERIVED_CODES.includes(err.code);
}

/**
 * Shown after a reset code is requested, whether or not the account exists,
 * whether or not it is confirmed, and whether or not it has a verified email.
 *
 * Conditional by construction ("if that account exists") so it is not a lie in
 * the case where nothing was sent, and it names the way out for a user who is
 * genuinely stuck — an unconfirmed account or one with no verified address
 * cannot self-serve and needs an administrator.
 */
export const RESET_CODE_REQUESTED_MESSAGE =
	'If that account exists, we have sent a verification code to its registered email address. ' +
	'Enter the code below to continue. If nothing arrives, contact your administrator.';

/**
 * The one answer to every credential failure on the sign-in form.
 *
 * The temporary-password sentence is here rather than on its own branch
 * deliberately. Cognito returns NotAuthorizedException both for a wrong
 * password and for a temporary password past its 40-day validity window, with
 * no field separating them, and genuine expiry has no other screen to surface
 * on — InitiateAuth fails outright, so the NEW_PASSWORD_REQUIRED challenge
 * never arrives and the set-password screen never renders (AL-470). Saying it
 * to everyone costs nothing and discloses nothing, because everyone gets it.
 */
const SIGN_IN_FAILURE =
	'We could not sign you in. Check your username and password. ' +
	'If you were sent a temporary password, it may no longer be valid — ' +
	'ask your administrator to issue a new one.';

/** Set a new password, either on first sign-in or after a reset. */
const NEW_PASSWORD_FAILURE = 'That password could not be accepted. Please try again.';

/**
 * Wrong code, expired code, unknown account and unconfirmed account all land
 * here, so the code step reveals nothing the email step already refused to.
 */
const RESET_CONFIRM_FAILURE =
	'That verification code is not valid. Check the code and try again, or request a new one.';

/** Map a failure onto our own wording. Cognito's own text is never shown. */
export function messageForAuthError(
	err: unknown,
	context: AuthMessageContext,
	policy: PasswordPolicy
): string {
	const code: AuthErrorCode = err instanceof AuthError ? err.code : 'UNKNOWN';

	// Every existence-derived failure resolves to this context's single answer,
	// before any per-code wording gets a chance to differentiate them.
	if (EXISTENCE_DERIVED_CODES.includes(code)) {
		switch (context) {
			case 'signIn':
				return SIGN_IN_FAILURE;
			case 'newPassword':
				return NEW_PASSWORD_FAILURE;
			case 'resetRequest':
				// Callers should absorb these via revealsAccountExistence() and
				// never reach here. Answer neutrally if one slips through.
				return RESET_CODE_REQUESTED_MESSAGE;
			case 'resetConfirm':
				return RESET_CONFIRM_FAILURE;
		}
	}

	switch (code) {
		case 'PASSWORD_POLICY':
			return describePasswordPolicy(policy);
		case 'CHALLENGE_EXPIRED':
			return 'This sign-in attempt has timed out. Please sign in again to set your password.';
		case 'CODE_MISMATCH':
		case 'CODE_EXPIRED':
			// Not collapsed for secrecy — collapsed because the distinction is
			// not actionable, and keeping them apart would make the unknown-account
			// case above stand out as the only generic one.
			return RESET_CONFIRM_FAILURE;
		case 'LIMIT_EXCEEDED':
			// Rate limiting is about this browser's behaviour, not the account,
			// so it is safe to name and unhelpful to hide.
			return 'Too many attempts. Please wait a few minutes and try again.';
		default:
			return context === 'resetRequest' || context === 'resetConfirm'
				? 'We could not reset your password. Please try again.'
				: 'Something went wrong. Please try again.';
	}
}
